import axios from "axios";
import axiosInstance from "./https/global";
import CONFIG from "../config";

const baseUrl = `${CONFIG.BASE_URL}/wayapay-payment-dispute/api/v2`;

const throwAxios = (error) => {
  if (axios.isAxiosError(error)) {
    throw error.response?.data || error;
  }
  throw new Error("An unexpected error occurred");
};

// Complaints (Merchant <-> WayaQuick): GET/PATCH .../complaint/admin/*
// No `status` filter exists on the list endpoint — only disputeType, search,
// page, size, sortBy, sortDir — so the Complaints tab filters by type instead.
export const getComplaints = async (params = {}) => {
  const queryParams = {};
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.disputeType) queryParams.disputeType = params.disputeType;
  if (params.search && params.search.trim())
    queryParams.search = params.search.trim();

  try {
    const response = await axiosInstance.get(`${baseUrl}/complaint/admin`, {
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const getComplaintById = async (id) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/complaint/admin/track/${id}`,
    );
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const getComplaintStats = async () => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/complaint/admin/stats`);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

// verdict "RESOLVED" -> PATCH .../resolve/{id} (shorthand, sets CLOSED server-side)
// any other verdict (e.g. "REJECTED") -> PATCH .../{id}?disputeStatus=<verdict>
export const resolveComplaint = async ({ id, verdict, note }) => {
  try {
    const response =
      verdict === "RESOLVED"
        ? await axiosInstance.patch(`${baseUrl}/complaint/admin/resolve/${id}`, {
            ...(note ? { note } : {}),
          })
        : await axiosInstance.patch(
            `${baseUrl}/complaint/admin/${id}`,
            null,
            { params: { disputeStatus: verdict, ...(note ? { note } : {}) } },
          );
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

// Chargebacks (Merchant <-> Customer): GET/PATCH .../dispute/admin/*
// `status` filter's expected values are unconfirmed against real data — assuming
// it matches disputeResolutionStatus (RESPONSE_NEEDED/UNDER_REVIEW/MERCHANT_WON/
// CUSTOMER_WON), same vocabulary the status filter dropdown offers.
export const getChargebacks = async (params = {}) => {
  const queryParams = {};
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.status) queryParams.status = params.status;
  if (params.search && params.search.trim())
    queryParams.search = params.search.trim();

  try {
    const response = await axiosInstance.get(`${baseUrl}/dispute/admin`, {
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const getChargebackById = async (id) => {
  try {
    const response = await axiosInstance.get(
      `${baseUrl}/dispute/admin/fetch/${id}`,
    );
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const getChargebackStats = async () => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/dispute/admin/stats`);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

// Only MERCHANT_WON/CUSTOMER_WON are accepted server-side; `message` is
// required for CUSTOMER_WON and stored as adminMessage on the record.
export const resolveChargeback = async ({ id, verdict, note }) => {
  try {
    const response = await axiosInstance.patch(
      `${baseUrl}/dispute/admin/resolve-dispute/${id}`,
      { status: verdict, ...(note ? { message: note } : {}) },
    );
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const createDispute = async (data) => {
  try {
    const response = await axiosInstance.post(`${baseUrl}/dispute`, data);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};
